<template>
    <div class="main-content">
        <div class="container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="back-link">
                    <a href="/#/singleCell/list"><i class="el-icon-back"></i> Back to Datasets</a>
                </div>
                <h1>{{ detailInfo.singleCell || 'Dataset Detail' }}</h1>
                <p class="lead">Detailed analysis and module associations for this single-cell dataset.</p>
            </div>

            <!-- Overview Card -->
            <div class="info-card">
                <div class="card-header">
                    <div class="title-row">
                        <span><i class="el-icon-info"></i> Dataset Overview</span>
                    </div>
                </div>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label"><i class="el-icon-price-tag"></i> Name</span>
                        <span class="value highlight">{{ detailInfo.name || detailInfo.singleCell }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-user"></i> Organism</span>
                        <span class="value">{{ detailInfo.organism }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-s-operation"></i> Tissue</span>
                        <span class="value">{{ detailInfo.tissue }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-first-aid-kit"></i> Condition</span>
                        <span class="value">{{ detailInfo.condition }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-office-building"></i> Data Source</span>
                        <span class="value">{{ detailInfo.dataSource }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-link"></i> Accession</span>
                        <span class="value">
                            <span v-if="!detailInfo.dataLink">{{ detailInfo.dataAccession }}</span>
                            <a v-else :href="detailInfo.dataLink" target="_blank">{{ detailInfo.dataAccession }}</a>
                        </span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-document"></i> PMID</span>
                        <span class="value">
                            <a :href="`https://pubmed.ncbi.nlm.nih.gov/${detailInfo.pmid}`" target="_blank">{{ detailInfo.pmid }}</a>
                        </span>
                    </div>
                    <div class="info-item full-width" v-if="detailInfo.paper">
                        <span class="label"><i class="el-icon-reading"></i> Paper</span>
                        <span class="value">{{ detailInfo.paper }}</span>
                    </div>
                    <div class="info-item full-width" v-if="detailInfo.notes">
                        <span class="label"><i class="el-icon-notebook-1"></i> Notes</span>
                        <span class="value">{{ detailInfo.notes }}</span>
                    </div>
                </div>
            </div>

            <el-tabs v-model="activeTab" class="mt-4 custom-tabs">
                <el-tab-pane label="Gene" name="Gene">
                    <!-- Gene Correlation Visualizations -->
                    <div class="section-container">
                        <h3 class="section-title"><i class="el-icon-picture-outline"></i> Data Overview</h3>
                        <el-row :gutter="24">
                            <el-col :span="12" v-if="detailInfo.umapPlot">
                                <div class="resource-card">
                                    <div class="resource-body">
                                        <iframe :src="getFullPath(detailInfo.umapPlot)" frameborder="0" class="resource-iframe" />
                                    </div>
                                    <div class="resource-footer">UMAP Plot</div>
                                </div>
                            </el-col>
                            <el-col :span="12" v-if="detailInfo.piePlot">
                                <div class="resource-card">
                                    <div class="resource-body">
                                        <iframe :src="getFullPath(detailInfo.piePlot)" frameborder="0" class="resource-iframe" />
                                    </div>
                                    <div class="resource-footer">Pie Plot</div>
                                </div>
                            </el-col>
                            <!-- Fallback for existing structure if fields are missing but in resources -->
                            <el-col :span="12" v-if="!detailInfo.umapPlot && htmlUmap">
                                <div class="resource-card">
                                    <div class="resource-body">
                                        <iframe :src="getFullPath(htmlUmap.url)" frameborder="0" class="resource-iframe" />
                                    </div>
                                    <div class="resource-footer">{{ htmlUmap.name }}</div>
                                </div>
                            </el-col>
                            <el-col :span="12" v-if="!detailInfo.piePlot && htmlCellProportion">
                                <div class="resource-card">
                                    <div class="resource-body">
                                        <iframe :src="getFullPath(htmlCellProportion.url)" frameborder="0" class="resource-iframe" />
                                    </div>
                                    <div class="resource-footer">{{ htmlCellProportion.name }}</div>
                                </div>
                            </el-col>
                        </el-row>
                    </div>

                    <div class="section-container mt-4">
                        <h3 class="section-title"><i class="el-icon-s-grid"></i> Gene Correlation</h3>

                        <!-- Filter Section -->
                        <div class="filter-container mb-3">
                            <el-form :inline="true" :model="geneFilters" class="demo-form-inline" size="small">
                                <el-form-item label="Adj. P-value">
                                    <el-input v-model="geneFilters.maxAdjustedP" placeholder="< Value"></el-input>
                                </el-form-item>
                                <el-form-item label="Correlation">
                                    <el-input v-model="geneFilters.minCorr" placeholder="> Value"></el-input>
                                </el-form-item>
                                <el-form-item label="Cell Type">
                                    <el-select v-model="geneFilters.cellType" placeholder="Select Cell Type" clearable>
                                        <el-option v-for="type in filterOptions.cellTypes" :key="type" :label="type" :value="type" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 1">
                                    <el-select v-model="geneFilters.gene1" placeholder="Select Gene 1" filterable clearable>
                                        <el-option v-for="item in filterOptions.gene1" :key="item" :label="item" :value="item" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 2">
                                    <el-select v-model="geneFilters.gene2" placeholder="Select Gene 2" filterable clearable>
                                        <el-option v-for="item in filterOptions.gene2" :key="item" :label="item" :value="item" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleGeneFilterSearch" icon="el-icon-search">Search</el-button>
                                    <el-button @click="handleGeneFilterReset" icon="el-icon-refresh">Reset</el-button>
                                    <el-button type="warning" size="small" icon="el-icon-download" @click="handleGeneExport">Download</el-button>
                                </el-form-item>
                            </el-form>
                        </div>

                        <div class="table-card">
                            <el-table :data="geneTableData" style="width: 100%" stripe>
                                <el-table-column prop="gene1" label="Gene 1" sortable>
                                    <template slot-scope="scope">
                                        <el-link type="primary" @click="handleGenePreview(scope.row.gene1)">{{ scope.row.gene1 }}</el-link>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="gene2" label="Gene 2" sortable>
                                    <template slot-scope="scope">
                                        <el-link type="primary" @click="handleGenePreview(scope.row.gene2)">{{ scope.row.gene2 }}</el-link>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="pvalue" label="P-value" sortable />
                                <el-table-column prop="adjustedP" label="Adj. P-value" sortable />
                                <el-table-column prop="corr" label="Correlation" sortable />
                                <el-table-column prop="cellType" label="Cell Type" sortable />
                                <el-table-column prop="tissue" label="Tissue" sortable />
                                <el-table-column prop="data" label="Data" sortable />
                                <el-table-column prop="type" label="Type" sortable />
                            </el-table>

                            <div class="pagination-wrapper">
                                <el-pagination
                                    @size-change="handleGeneSizeChange"
                                    @current-change="handleGenePageChange"
                                    :current-page="genePagination.pageNum"
                                    :page-sizes="[10, 20, 50]"
                                    :page-size="genePagination.pageSize"
                                    layout="total, sizes, prev, pager, next"
                                    :total="genePagination.total">
                                </el-pagination>
                            </div>
                        </div>
                    </div>

                    <!-- Draw Function -->
                    <div class="section-container mt-4" style="margin-top: 20px;">
                        <h3 class="section-title"><i class="el-icon-edit-outline"></i> Draw Function</h3>
                        <div class="panel">
                            <el-form :inline="true" :model="drawForm" class="demo-form-inline">
                                <el-form-item label="Gene 1">
                                    <el-select
                                        v-model="drawForm.gene1"
                                        filterable
                                        placeholder="Select Gene 1">
                                        <el-option
                                            v-for="item in filterOptions.gene1"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 2">
                                    <el-select
                                        v-model="drawForm.gene2"
                                        filterable
                                        placeholder="Select Gene 2">
                                        <el-option
                                            v-for="item in filterOptions.gene2"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Cell Type">
                                    <el-select v-model="drawForm.cellType" placeholder="Select Cell Type">
                                        <el-option v-for="type in filterOptions.cellTypes" :key="type" :label="type" :value="type" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleDraw" :loading="drawLoading">Draw</el-button>
                                </el-form-item>
                            </el-form>

                            <div v-if="drawResultImage" class="draw-result mt-4" style="text-align: center;">
                                <el-image
                                    :src="getFullPath(drawResultImage)"
                                    style="max-height: 600px; max-width: 100%;"
                                    fit="contain"
                                    :preview-src-list="[getFullPath(drawResultImage)]">
                                    <div slot="placeholder" class="image-slot">
                                        Loading<span class="dot">...</span>
                                    </div>
                                </el-image>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="Module" name="Module">
                    <!-- Module Summary Table -->
                    <div class="section-container mt-4">
                        <h3 class="section-title"><i class="el-icon-s-grid"></i> Module Summary</h3>
                        
                        <!-- Module Filters -->
                        <div class="filter-container mb-3">
                            <el-form :inline="true" :model="moduleFilters" size="small">
                                <el-form-item label="Cell Type">
                                    <el-select v-model="moduleFilters.cellType" placeholder="Select Cell Type" clearable filterable @change="handleModuleSummarySearch">
                                        <el-option v-for="type in moduleFilterOptions.cellTypes" :key="type" :label="type" :value="type" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleModuleSummarySearch" icon="el-icon-search">Search</el-button>
                                    <el-button @click="handleModuleSummaryReset" icon="el-icon-refresh">Reset</el-button>
                                </el-form-item>
                            </el-form>
                        </div>

                        <div class="table-card">
                            <el-table :data="moduleSummaryTableData" style="width: 100%" stripe>
                                <el-table-column prop="data" label="Data" sortable />
                                <el-table-column prop="moduleName" label="Module_Name" sortable>
                                    <template slot-scope="scope">
                                        <el-button type="text" @click="handleModuleClick(scope.row)">{{ scope.row.moduleName }}</el-button>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="cellType" label="Cell_Type" sortable />
                                <el-table-column prop="numberOfGenes" label="Number_of_Genes" sortable />
                                <el-table-column prop="topGo" label="Top_GO" show-overflow-tooltip />
                                <el-table-column prop="topKegg" label="Top_Kegg" show-overflow-tooltip />
                                <el-table-column prop="topTf" label="Top_TF" show-overflow-tooltip />
                            </el-table>
                            <div class="pagination-wrapper">
                                <el-pagination
                                    @size-change="handleModuleSummarySizeChange"
                                    @current-change="handleModuleSummaryPageChange"
                                    :current-page="moduleSummaryPagination.pageNum"
                                    :page-sizes="[10, 20, 50]"
                                    :page-size="moduleSummaryPagination.pageSize"
                                    layout="total, sizes, prev, pager, next"
                                    :total="moduleSummaryPagination.total">
                                </el-pagination>
                            </div>
                        </div>
                    </div>

                    <!-- Modules Section -->

                    <!-- Module Specific Detail Section -->
                    <transition name="el-fade-in">
                        <div id="module-detail-section" v-if="moduleDetailData || moduleDetailLoading" class="mt-4" v-loading="moduleDetailLoading">
                            <!-- Header -->
                            <div class="section-header" v-if="currentModuleRow">
                                <h3 class="section-title">Module Detail: {{ currentModuleRow.moduleName }}</h3>
                            </div>

                            <div v-if="moduleDetailData">
                                <div class="sub-section mt-4">
                                    <div class="section-header">
                                        <h4 class="sub-title">Gene Correlation</h4>
                                        <div class="header-actions">
                                            <el-button v-if="moduleDetailData.geneTableDownloadUrl" type="text"
                                                @click="downloadFile(moduleDetailData.geneTableDownloadUrl)" icon="el-icon-download">
                                                Download
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="table-card plain">
                                        <el-table :data="pagedModuleGeneData" style="width: 100%" stripe border size="small">
                                            <el-table-column prop="geneSymbol" label="Gene.Symbol" sortable />
                                            <el-table-column prop="module" label="module" sortable />
                                            <el-table-column prop="cellType" label="cell_type" sortable />
                                            <el-table-column prop="ensemblId" label="Ensembl.ID" />
                                            <el-table-column prop="geneLocation" label="Gene.Location" />
                                            <el-table-column prop="geneType" label="Gene.Type" />
                                        </el-table>
                                        <div class="pagination-wrapper">
                                            <el-pagination
                                                @size-change="handleModuleGeneSizeChange"
                                                @current-change="handleModuleGenePageChange"
                                                :current-page="moduleGenePagination.currentPage"
                                                :page-sizes="[10, 20, 50]"
                                                :page-size="moduleGenePagination.pageSize"
                                                layout="total, sizes, prev, pager, next"
                                                :total="moduleGenePagination.total">
                                            </el-pagination>
                                        </div>
                                    </div>
                                </div>

                                <!-- 2. Charts (GO & KEGG) -->
                                <div class="sub-section mt-4">
                                    <h4 class="sub-title">Pathway Enrichment</h4>
                                    <el-row :gutter="20">
                                        <el-col :span="12">
                                            <div class="chart-card">
                                                <div class="chart-header" style="display: flex; justify-content: space-between; align-items: center; padding: 10px;">
                                                    <span style="font-weight: bold;">GO Barplot</span>
                                                    <el-button v-if="moduleDetailData.goResultsDownloadUrl" type="text" size="small"
                                                        @click="downloadFile(moduleDetailData.goResultsDownloadUrl)" icon="el-icon-download">
                                                        Download Results
                                                    </el-button>
                                                </div>
                                                <div class="chart-content" style="text-align: center; padding: 10px;">
                                                    <el-image
                                                        v-if="moduleDetailData.goBarplotUrl"
                                                        :src="getFullPath(moduleDetailData.goBarplotUrl)"
                                                        fit="contain"
                                                        style="max-height: 400px;"
                                                        :preview-src-list="[getFullPath(moduleDetailData.goBarplotUrl)]">
                                                    </el-image>
                                                    <div v-else class="no-data">No Image Available</div>
                                                </div>
                                            </div>
                                        </el-col>
                                        <el-col :span="12">
                                            <div class="chart-card">
                                                <div class="chart-header" style="display: flex; justify-content: space-between; align-items: center; padding: 10px;">
                                                    <span style="font-weight: bold;">KEGG Barplot</span>
                                                    <el-button v-if="moduleDetailData.keggResultsDownloadUrl" type="text" size="small"
                                                        @click="downloadFile(moduleDetailData.keggResultsDownloadUrl)" icon="el-icon-download">
                                                        Download Results
                                                    </el-button>
                                                </div>
                                                <div class="chart-content" style="text-align: center; padding: 10px;">
                                                     <el-image
                                                        v-if="moduleDetailData.keggBarplotUrl"
                                                        :src="getFullPath(moduleDetailData.keggBarplotUrl)"
                                                        fit="contain"
                                                        style="max-height: 400px;"
                                                        :preview-src-list="[getFullPath(moduleDetailData.keggBarplotUrl)]">
                                                    </el-image>
                                                    <div v-else class="no-data">No Image Available</div>
                                                </div>
                                            </div>
                                        </el-col>
                                    </el-row>
                                </div>

                                <!-- 3. Enriched TF/Motif Table -->
                                <div class="sub-section mt-4">
                                    <div class="section-header">
                                        <h4 class="sub-title">Enriched TF/Motif</h4>
                                        <div class="header-actions">
                                            <el-button v-if="moduleDetailData.tfTableDownloadUrl" type="text"
                                                @click="downloadFile(moduleDetailData.tfTableDownloadUrl)" icon="el-icon-download">
                                                Download
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="table-card plain">
                                        <el-table :data="pagedModuleTfData" style="width: 100%" stripe border size="small">
                                            <el-table-column prop="motif" label="motif" sortable />
                                            <el-table-column prop="nes" label="NES" sortable />
                                            <el-table-column prop="auc" label="AUC" sortable />
                                            <el-table-column prop="tfHighConf" label="TF_highConf" sortable />
                                            <el-table-column prop="tfLowConf" label="TF_lowConf" sortable />
                                            <el-table-column prop="nenrGenes" label="nEnrGenes" sortable />
                                            <el-table-column prop="rankAtMax" label="rankAtMax" sortable />
                                            <el-table-column prop="enrichedGenes" label="enrichedGenes" show-overflow-tooltip />
                                        </el-table>
                                        <div class="pagination-wrapper">
                                            <el-pagination
                                                @size-change="handleModuleTfSizeChange"
                                                @current-change="handleModuleTfPageChange"
                                                :current-page="moduleTfPagination.currentPage"
                                                :page-sizes="[10, 20, 50]"
                                                :page-size="moduleTfPagination.pageSize"
                                                layout="total, sizes, prev, pager, next"
                                                :total="moduleTfPagination.total">
                                            </el-pagination>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="empty-state mt-4">
                            <i class="el-icon-mouse"></i>
                            <p>Click a module name from the table above to view detailed analysis.</p>
                        </div>
                    </transition>
                </el-tab-pane>
            </el-tabs>

            <!-- Gene Image Preview Dialog -->
            <el-dialog :visible.sync="genePreviewVisible" title="Gene Preview" width="50%">
                <div v-if="genePreviewUrl" style="text-align: center;">
                     <el-image :src="getFullPath(genePreviewUrl)" fit="contain">
                        <div slot="placeholder" class="image-slot">
                            Loading<span class="dot">...</span>
                        </div>
                    </el-image>
                </div>
            </el-dialog>
        </div>
        <div class="spacer"></div>
    </div>
</template>

<script>
export default {
    name: "results",
    data() {
        return {
            activeTab: 'Gene',
            source: this.$route.query.source || "",
            target: this.$route.query.target || "",
            detailInfo: {
                imageList: [],
                module: undefined,
            },
            searchForm: {

            },
            // moduleTableData: [],
            // modulePagination: {
            //     currentPage: 1,
            //     pageSize: 10,
            //     total: 0
            // },
            // Module Summary Data
            moduleSummaryTableData: [],
            moduleSummaryPagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            moduleFilters: {
                cellType: ''
            },
            moduleFilterOptions: {
                cellTypes: []
            },

            // Module Detail Data
            moduleDetailData: null,
            moduleDetailLoading: false,
            currentModuleRow: null,

            moduleGenePagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },

            // Module TF Table Pagination (Frontend)
            moduleTfPagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },

            moduleBaseInfo: null,
            diseaseTableData: [],
            filteredDiseases: [],
            diseasePagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            diseaseSearchText: '',
            diseaseSortProp: 'trait',
            diseaseSortOrder: 'ascending',
            geneScoreTableData: [],
            filteredGeneScores: [],
            geneScorePagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            geneScoreSearchText: '',
            geneScoreSortProp: 'gene',
            geneScoreSortOrder: 'ascending',
            enrichmentTableData: [],
            filteredEnrichments: [],
            enrichmentPagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            enrichmentSearchText: '',
            enrichmentSortProp: 'database',
            enrichmentSortOrder: 'ascending',
            activeRow: null, // 新增这行
            htmlCellProportion: undefined,
            htmlUmap: undefined,

            // Gene Tab Data
            geneTableData: [],
            genePagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            geneFilters: {
                maxAdjustedP: '',
                minCorr: '',
                cellType: '',
                gene1: '',
                gene2: ''
            },
            filterOptions: {
                gene1: [],
                gene2: [],
                cellTypes: []
            },
            drawForm: {
                gene1: '',
                gene2: '',
                cellType: ''
            },
            drawResultImage: '',
            // geneOptions: [], // Removed as part of cleanup
            // cellTypeOptions: [], // Removed as part of cleanup
            // geneLoading: false, // Removed as part of cleanup
            drawLoading: false,
            genePreviewVisible: false,
            genePreviewUrl: ''
        };
    },

    computed: {
        pagedModuleGeneData() {
            if (!this.moduleDetailData || !this.moduleDetailData.geneTable) return [];
            const start = (this.moduleGenePagination.currentPage - 1) * this.moduleGenePagination.pageSize;
            const end = start + this.moduleGenePagination.pageSize;
            return this.moduleDetailData.geneTable.slice(start, end);
        },
        pagedModuleTfData() {
            if (!this.moduleDetailData || !this.moduleDetailData.tfTable) return [];
            const start = (this.moduleTfPagination.currentPage - 1) * this.moduleTfPagination.pageSize;
            const end = start + this.moduleTfPagination.pageSize;
            return this.moduleDetailData.tfTable.slice(start, end);
        }
    },

    created() {
        if (this.$route.query.tab) {
            this.activeTab = this.$route.query.tab;
        }
        this.getDetail();
        this.getFilterOptions();
        this.getModuleFilterOptions();
        this.getGeneData();
        this.getModuleSummaryData();
        this.checkInitialModule();
    },

    methods: {
        async getFilterOptions() {
            try {
                const res = await this.$ajax.get(`/singleCell/corr/filters?singleCellId=${this.$route.params.id}`);
                if (res.code === 200) {
                    this.filterOptions = res.data;
                }
            } catch (error) {
                console.error("Failed to fetch filter options:", error);
            }
        },

        async getGeneData() {
            try {
                const params = new URLSearchParams();
                params.append('pageNum', this.genePagination.pageNum);
                params.append('pageSize', this.genePagination.pageSize);
                params.append('singleCellId', this.$route.params.id);

                if (this.geneFilters.maxAdjustedP) params.append('maxAdjustedP', this.geneFilters.maxAdjustedP);
                if (this.geneFilters.minCorr) params.append('minCorr', this.geneFilters.minCorr);
                if (this.geneFilters.cellType) params.append('cellType', this.geneFilters.cellType);
                if (this.geneFilters.gene1) params.append('gene1', this.geneFilters.gene1);
                if (this.geneFilters.gene2) params.append('gene2', this.geneFilters.gene2);

                const res = await this.$ajax.get(`/singleCell/corr/page?${params.toString()}`);
                if (res.code === 200) {
                    this.geneTableData = res.data.records || [];
                    this.genePagination.total = res.data.total || 0;
                }
            } catch (error) {
                console.error("Failed to fetch gene table data:", error);
            }
        },

        async getModuleSummaryData() {
            try {
                const params = new URLSearchParams();
                params.append('pageNum', this.moduleSummaryPagination.pageNum);
                params.append('pageSize', this.moduleSummaryPagination.pageSize);
                params.append('singleCellId', this.$route.params.id);
                if (this.moduleFilters.cellType) params.append('cellType', this.moduleFilters.cellType);

                const res = await this.$ajax.get(`/singleCell/module/summary/page?${params.toString()}`);
                if (res.code === 200) {
                    this.moduleSummaryTableData = res.data.records || [];
                    this.moduleSummaryPagination.total = res.data.total || 0;
                }
            } catch (error) {
                console.error("Failed to fetch module summary data:", error);
            }
        },

        async getModuleFilterOptions() {
            try {
                const res = await this.$ajax.get(`/singleCell/module/filters?singleCellId=${this.$route.params.id}`);
                if (res.code === 200) {
                    this.moduleFilterOptions = res.data;
                }
            } catch (error) {
                console.error("Failed to fetch module filter options:", error);
            }
        },

        handleModuleSummarySearch() {
            this.moduleSummaryPagination.pageNum = 1;
            this.getModuleSummaryData();
        },

        handleModuleSummaryReset() {
            this.moduleFilters.cellType = '';
            this.handleModuleSummarySearch();
        },

        checkInitialModule() {
            const queryModule = this.$route.query.moduleName || this.$route.query.moduleNumber;
            const queryCellType = this.$route.query.cellType;
            
            if (queryModule && queryCellType) {
                // If we have both, we can directly load the module detail
                this.handleModuleClick({
                    moduleName: queryModule,
                    cellType: queryCellType
                });
            } else if (queryModule) {
                // If we only have moduleName, we might need to search the first page as fallback
                // or just wait for summary data and find it there (existing logic moved here)
                this.$nextTick(() => {
                    const targetRow = this.moduleSummaryTableData.find(row => {
                        const queryMod = queryModule.toString().toLowerCase();
                        const rowMod = row.moduleName.toString().toLowerCase();
                        return rowMod === queryMod || 
                               rowMod === `module ${queryMod}` || 
                               rowMod.includes(queryMod);
                    });
                    if (targetRow) {
                        this.handleModuleClick(targetRow);
                    }
                });
            }
        },

        handleModuleSummarySizeChange(size) {
            this.moduleSummaryPagination.pageSize = size;
            this.getModuleSummaryData();
        },

        handleModuleSummaryPageChange(page) {
            this.moduleSummaryPagination.pageNum = page;
            this.getModuleSummaryData();
        },

        async handleModuleClick(row) {
            this.currentModuleRow = row;
            this.moduleDetailLoading = true;
            this.moduleDetailData = null;

            // Reset pagination
            this.moduleGenePagination.currentPage = 1;
            this.moduleTfPagination.currentPage = 1;

            try {
                // Extract module number from moduleName (e.g., "Module 1" -> "1")
                let moduleNum = row.moduleName;
                const match = row.moduleName.match(/\d+/);
                if (match) {
                    moduleNum = match[0];
                }

                // Construct query string manually as requested
                const queryParams = new URLSearchParams();
                queryParams.append('singleCellId', this.$route.params.id);
                queryParams.append('cellType', row.cellType);
                queryParams.append('moduleNum', moduleNum);

                const res = await this.$ajax.get(`/singleCell/module/detail?${queryParams.toString()}`);

                if (res.code === 200) {
                    this.moduleDetailData = res.data;
                    this.moduleGenePagination.total = (res.data.geneTable || []).length;
                    this.moduleTfPagination.total = (res.data.tfTable || []).length;

                    // Scroll to detail section
                    this.$nextTick(() => {
                        const element = document.getElementById('module-detail-section');
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                        }
                    });
                } else {
                    this.$message.error(res.message || 'Failed to fetch module details');
                }
            } catch (error) {
                console.error("Failed to fetch module detail:", error);
                this.$message.error("Failed to fetch module detail");
            } finally {
                this.moduleDetailLoading = false;
            }
        },

        // Module Gene Table Pagination Handlers
        handleModuleGeneSizeChange(size) {
            this.moduleGenePagination.pageSize = size;
            this.moduleGenePagination.currentPage = 1;
        },
        handleModuleGenePageChange(page) {
            this.moduleGenePagination.currentPage = page;
        },

        // Module TF Table Pagination Handlers
        handleModuleTfSizeChange(size) {
            this.moduleTfPagination.pageSize = size;
            this.moduleTfPagination.currentPage = 1;
        },
        handleModuleTfPageChange(page) {
            this.moduleTfPagination.currentPage = page;
        },

        handleGeneFilterSearch() {
            this.genePagination.pageNum = 1;
            this.getGeneData();
        },

        handleGeneFilterReset() {
            this.geneFilters = {
                maxAdjustedP: '',
                minCorr: '',
                cellType: '',
                gene1: '',
                gene2: ''
            };
            this.handleGeneFilterSearch();
        },

        handleGeneSizeChange(size) {
            this.genePagination.pageSize = size;
            this.getGeneData();
        },

        handleGenePageChange(page) {
            this.genePagination.pageNum = page;
            this.getGeneData();
        },

        handleGeneExport() {
            const params = new URLSearchParams();
            params.append('singleCellId', this.$route.params.id);
            if (this.geneFilters.maxAdjustedP) params.append('maxAdjustedP', this.geneFilters.maxAdjustedP);
            if (this.geneFilters.minCorr) params.append('minCorr', this.geneFilters.minCorr);
            if (this.geneFilters.cellType) params.append('cellType', this.geneFilters.cellType);
            if (this.geneFilters.gene1) params.append('gene1', this.geneFilters.gene1);
            if (this.geneFilters.gene2) params.append('gene2', this.geneFilters.gene2);

            const url = `/api/singleCell/corr/export?${params.toString()}`;
            window.open(url, '_blank');
        },

        handleGenePreview(gene) {
            if (!gene) return;
            // Pattern: /api/file/common?path=single_cell/{singleCell}/fig/gene/{gene}.png
            // Note: detailInfo.name is used for singleCell part based on prompt "single_cell/Human_Heart_Selewa/..."
            // Assuming detailInfo.name holds "Human_Heart_Selewa" or similar
            const singleCellName = this.detailInfo.name || this.detailInfo.singleCell;
            this.genePreviewUrl = `api/file/common?path=single_cell/${singleCellName}/fig/gene/${gene}.png`;
            this.genePreviewVisible = true;
        },

        // queryGene1, queryGene2, queryGene removed as we now use filterOptions

        async handleDraw() {
            if (!this.drawForm.gene1 || !this.drawForm.gene2 || !this.drawForm.cellType) {
                this.$message.warning('Please select Gene 1, Gene 2 and Cell Type');
                return;
            }

            this.drawLoading = true;
            try {
                const singleCellName = this.detailInfo.name || this.detailInfo.singleCell;
                const params = {
                    gene1: this.drawForm.gene1,
                    gene2: this.drawForm.gene2,
                    cellType: this.drawForm.cellType,
                    singleCell: singleCellName
                };

                // Assuming POST request for draw, or GET with params
                // The prompt said "call interface /singleCell/draw,传入private String gene1..."
                // Usually this implies POST or query params. I'll try POST first as it's an action.
                // Wait, "传入... gene1; ... gene2..." sounds like a DTO, so likely POST.
                const res = await this.$ajax.post('/singleCell/draw', params);

                if (res.code === 200) {
                     // Result is an image path
                    this.drawResultImage = res.data;
                } else {
                    this.$message.error(res.message || 'Draw failed');
                }
            } catch (error) {
                console.error(error);
                this.$message.error('An error occurred during drawing');
            } finally {
                this.drawLoading = false;
            }
        },

        // updateModuleTable() {
        //     if (!this.detailInfo || !this.detailInfo.modules) {
        //         this.moduleTableData = [];
        //         this.modulePagination.total = 0;
        //         return;
        //     }

        //     this.modulePagination.total = this.detailInfo.modules.length;
        //     const start = (this.modulePagination.currentPage - 1) * this.modulePagination.pageSize;
        //     const end = start + this.modulePagination.pageSize;
        //     this.moduleTableData = this.detailInfo.modules.slice(start, end);
        // },

        // handleModuleSizeChange(size) {
        //     this.modulePagination.pageSize = size;
        //     this.updateModuleTable();
        // },

        // handleModulePageChange(page) {
        //     this.modulePagination.currentPage = page;
        //     this.updateModuleTable();
        // },

        handleView(row) {
            this.activeRow = row; // 新增这行

            const moduleNumber = row.moduleName.match(/\d+/);
            if (moduleNumber) {
                this.handleModuleChange(parseInt(moduleNumber[0]));
            }
        },

        highlightActiveRow({ row }) {
            const isHighlight = this.activeRow && this.activeRow.moduleName === row.moduleName
            return isHighlight ? 'highlight-row' : '';
        },

        async getDetail() {
            const res = await this.$ajax.get(`/singleCell/detail/${this.$route.params.id}`);
            if (res.code !== 200) {
                this.$message.error(res.message);
                return;
            }
            this.detailInfo = res.data;
            const cellProportion = res.data.resources.filter(it => it.name == "Cell Proportion")
            if (cellProportion && cellProportion.length > 0) {
                this.htmlCellProportion = cellProportion[0]
            }
            const umap = res.data.resources.filter(it => it.name == "UMAP")
            if (umap && umap.length > 0) {
                this.htmlUmap = umap[0]
            }

            // Extract cell types from modules
            if (this.detailInfo.modules && this.detailInfo.modules.length > 0) {
                const types = new Set();
                this.detailInfo.modules.forEach(m => {
                    if (m.cellType) types.add(m.cellType);
                });
                this.cellTypeOptions = Array.from(types);
            }

            // if (moduleNumber) {
            //     const row = this.detailInfo.modules.find(item => item.moduleName === `Module_${moduleNumber}`) || null;
            //     if (row) {
            //         this.handleView(row)
            //     }
            // }

            // this.updateModuleTable(); // 添加这行
        },

        handleShowWarningText() {
            this.showWarningText = !this.showWarningText;
        },

        getFullPath(path) {
            if (!path) {
                return path;
            }
            if (path.startsWith('http') || path.startsWith('//')) {
                return path;
            }
            // 获取当前域名
            const baseUrl = window.location.origin;
            // 确保path是以/开头的
            const normalizedPath = path.startsWith('/') ? path : `/${path}`;
            return `${baseUrl}${normalizedPath}`;
        },

        async handleModuleChange(value) {
            if (!value) return;

            try {
                const res = await this.$ajax.get(`/mapping/${this.$route.params.id}/modules?number=${value}`);
                if (res.code !== 200) {
                    this.$message.error(res.message);
                    return;
                }
                this.moduleBaseInfo = res.data;
                this.updateDiseaseTable();
                this.updateGeneScoreTable();
                this.updateEnrichmentTable(); // 新增这行
            } catch (error) {
                this.$message.error('获取模块资源失败');
                console.error(error);
            }
        },
        updateDiseaseTable() {
            if (!this.moduleBaseInfo || !this.moduleBaseInfo.diseases) {
                this.diseaseTableData = [];
                this.filteredDiseases = [];
                this.diseasePagination.total = 0;
                return;
            }

            // 过滤数据
            this.filteredDiseases = this.moduleBaseInfo.diseases.filter(disease => {
                if (!this.diseaseSearchText) return true;
                return (disease.trait && disease.trait.includes(this.diseaseSearchText)) ||
                    (disease.meshTerm && disease.meshTerm.includes(this.diseaseSearchText));
            });

            // 排序数据
            if (this.diseaseSortProp) {
                this.filteredDiseases.sort((a, b) => {
                    const valA = a[this.diseaseSortProp];
                    const valB = b[this.diseaseSortProp];
                    if (valA === valB) return 0;
                    return this.diseaseSortOrder === 'ascending'
                        ? (valA < valB ? -1 : 1)
                        : (valA > valB ? -1 : 1);
                });
            }

            // 更新分页信息
            this.diseasePagination.total = this.filteredDiseases.length;

            // 获取当前页数据
            const start = (this.diseasePagination.currentPage - 1) * this.diseasePagination.pageSize;
            const end = start + this.diseasePagination.pageSize;
            this.diseaseTableData = this.filteredDiseases.slice(start, end);
        },
        handleDiseaseSearch() {
            this.diseasePagination.currentPage = 1;
            this.updateDiseaseTable();
        },

        handleDiseaseSort({ prop, order }) {
            this.diseaseSortProp = prop;
            this.diseaseSortOrder = order;
            this.updateDiseaseTable();
        },

        handleDiseaseSizeChange(size) {
            this.diseasePagination.pageSize = size;
            this.updateDiseaseTable();
        },

        handleDiseasePageChange(page) {
            this.diseasePagination.currentPage = page;
            this.updateDiseaseTable();
        },
        updateGeneScoreTable() {
            if (!this.moduleBaseInfo || !this.moduleBaseInfo.geneScores) {
                this.geneScoreTableData = [];
                this.filteredGeneScores = [];
                this.geneScorePagination.total = 0;
                return;
            }

            // 过滤数据
            this.filteredGeneScores = this.moduleBaseInfo.geneScores.filter(gene => {
                if (!this.geneScoreSearchText) return true;
                return (gene.ensembleId && gene.ensembleId.includes(this.geneScoreSearchText)) ||
                    (gene.gene && gene.gene.includes(this.geneScoreSearchText));
            });

            // 排序数据
            if (this.geneScoreSortProp) {
                this.filteredGeneScores.sort((a, b) => {
                    const valA = a[this.geneScoreSortProp];
                    const valB = b[this.geneScoreSortProp];
                    if (valA === valB) return 0;
                    return this.geneScoreSortOrder === 'ascending'
                        ? (valA < valB ? -1 : 1)
                        : (valA > valB ? -1 : 1);
                });
            }

            // 更新分页信息
            this.geneScorePagination.total = this.filteredGeneScores.length;

            // 获取当前页数据
            const start = (this.geneScorePagination.currentPage - 1) * this.geneScorePagination.pageSize;
            const end = start + this.geneScorePagination.pageSize;
            this.geneScoreTableData = this.filteredGeneScores.slice(start, end);
        },
        handleGeneScoreSearch() {
            this.geneScorePagination.currentPage = 1;
            this.updateGeneScoreTable();
        },

        handleGeneScoreSort({ prop, order }) {
            this.geneScoreSortProp = prop;
            this.geneScoreSortOrder = order;
            this.updateGeneScoreTable();
        },

        handleGeneScoreSizeChange(size) {
            this.geneScorePagination.pageSize = size;
            this.updateGeneScoreTable();
        },

        handleGeneScorePageChange(page) {
            this.geneScorePagination.currentPage = page;
            this.updateGeneScoreTable();
        },
        updateEnrichmentTable() {
            if (!this.moduleBaseInfo || !this.moduleBaseInfo.enrichmentAnalyses) {
                this.enrichmentTableData = [];
                this.filteredEnrichments = [];
                this.enrichmentPagination.total = 0;
                return;
            }

            // 过滤数据
            this.filteredEnrichments = this.moduleBaseInfo.enrichmentAnalyses.filter(item => {
                if (!this.enrichmentSearchText) return true;
                return Object.values(item).some(val =>
                    val && val.toString().includes(this.enrichmentSearchText)
                );
            });

            // 排序数据
            if (this.enrichmentSortProp) {
                this.filteredEnrichments.sort((a, b) => {
                    const valA = a[this.enrichmentSortProp];
                    const valB = b[this.enrichmentSortProp];
                    if (valA === valB) return 0;
                    return this.enrichmentSortOrder === 'ascending'
                        ? (valA < valB ? -1 : 1)
                        : (valA > valB ? -1 : 1);
                });
            }

            // 更新分页信息
            this.enrichmentPagination.total = this.filteredEnrichments.length;

            // 获取当前页数据
            const start = (this.enrichmentPagination.currentPage - 1) * this.enrichmentPagination.pageSize;
            const end = start + this.enrichmentPagination.pageSize;
            this.enrichmentTableData = this.filteredEnrichments.slice(start, end);
        },

        // handleEnrichmentSearch() {
        //     this.enrichmentPagination.currentPage = 1;
        //     this.updateEnrichmentTable();
        // },

        // handleEnrichmentSort({ prop, order }) {
        //     this.enrichmentSortProp = prop;
        //     this.enrichmentSortOrder = order;
        //     this.updateEnrichmentTable();
        // },

        // handleEnrichmentSizeChange(size) {
        //     this.enrichmentPagination.pageSize = size;
        //     this.updateEnrichmentTable();
        // },

        handleEnrichmentPageChange(page) {
            this.enrichmentPagination.currentPage = page;
            this.updateEnrichmentTable();
        },
        getCsvFile(htmlIndex) {
            if (!this.moduleBaseInfo.enrichmentAnalyses) return null;
            const csvFiles = this.moduleBaseInfo.enrichmentAnalyses.filter(it => it.type === 'csv');
            return csvFiles[htmlIndex] || null;
        },
        downloadFile(url) {
            const link = document.createElement('a')
            link.href = this.getFullPath(url) // Ensure full path is used

            // Extract filename from URL
            // If url contains 'path=' (API style), try to extract from there
            let filename = 'downloaded-file';
            if (url.includes('path=')) {
                try {
                    const urlObj = new URL(url, window.location.origin);
                    const pathParam = urlObj.searchParams.get('path');
                    if (pathParam) {
                        filename = pathParam.split('/').pop();
                    }
                } catch (e) {
                    // Fallback if URL parsing fails
                    filename = url.split('/').pop().split('?')[0];
                }
            } else {
                 filename = url.split('/').pop().split('?')[0];
            }

            link.download = filename || 'downloaded-file'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }
};
</script>

<style lang="less" scoped>
:root {
    --bg: #dfe4ed;
    --panel: #f7f9fc;
    --navy: #0e1a28;
    --muted: #4a586b;
    --accent: #5d87b5;
    --border: #c7d1dd;
    --shadow: 0 20px 44px rgba(14, 26, 40, 0.18);
    --primary: #0e1a28;
}

.main-content {
    background: #dfe4ed;
    min-height: 100vh;
    padding-bottom: 60px;
    font-family: 'Inter', sans-serif;
    color: #0e1a28;
}

.container {
    padding: 0 20px;
}

/* Header Section */
.header-section {
    padding: 30px 0 20px;

    .back-link {
        margin-bottom: 12px;
        a {
            color: var(--accent);
            text-decoration: none;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: transform 0.2s;
            &:hover {
                transform: translateX(-4px);
            }
        }
    }

    h1 {
        font-family: 'Playfair Display', serif;
        font-size: 36px;
        color: var(--navy);
        margin: 0 0 8px;
        font-weight: 700;
        letter-spacing: -0.5px;
    }

    .lead {
        font-size: 16px;
        color: var(--muted);
        max-width: 700px;
        line-height: 1.5;
    }
}

/* Info Card */
.info-card {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    padding: 24px;
    margin-bottom: 24px;

    .card-header {
        font-family: 'Playfair Display', serif;
        font-size: 20px;
        font-weight: 700;
        color: var(--navy);
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 10px;
        i { color: var(--accent); }
    }

    .info-grid {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .info-item {
            display: flex;
            flex-direction: row;
            align-items: baseline;
            gap: 20px;
            width: 100%;

            .label {
                font-size: 14px;
                font-weight: 600;
                color: var(--muted);
                width: 140px;
                flex-shrink: 0;
                display: flex;
                align-items: center;
                gap: 8px;

                i {
                    color: var(--accent);
                    opacity: 0.8;
                }
            }

            .value {
                flex: 1;
                font-size: 15px;
                color: var(--navy);
                line-height: 1.6;

                &.highlight {
                    font-weight: 600;
                    color: var(--primary);
                    font-size: 16px;
                }

                a {
                    color: var(--accent);
                    text-decoration: none;
                    &:hover { text-decoration: underline; }
                }
            }
        }
    }

    &.secondary {
        background: rgba(255,255,255,0.6);
        padding: 20px;
        box-shadow: none;
    }
}

/* Section Styling */
.section-container {
    .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
    }

    .section-title {
        font-family: 'Playfair Display', serif;
        font-size: 24px;
        font-weight: 700;
        color: var(--navy);
        margin: 0 0 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        i { color: var(--accent); }
    }
}

/* Resource Cards */
.resource-card {
    background: white;
    border-radius: 15px;
    border: 1px solid var(--border);
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
    margin-bottom: 20px;
    transition: transform 0.3s;

    &:hover {
        transform: translateY(-5px);
    }

    .resource-body {
        height: 450px;
        background: #f8fafc;
        overflow: auto; /* Enable scrolling */

        iframe {
            width: 100%;
            height: 100%;
            min-width: 100%; /* Ensure content can expand */
            min-height: 100%;
        }
    }

    .resource-footer {
        padding: 14px 20px;
        background: white;
        font-weight: 700;
        color: var(--navy);
        border-top: 1px solid var(--border);
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &.full-width {
        .resource-body { height: 600px; }
    }

    &.mini {
        .resource-body { height: 350px; }
    }
}

/* Table Card */
.table-card {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    overflow: hidden;
    padding: 16px;

    &.plain {
        box-shadow: none;
        padding: 0;
        border: none;
        background: transparent;
    }
}

/* Pagination Wrapper */
.pagination-wrapper {
    margin-top: 12px;
    text-align: right;
    padding-top: 10px;
    // border-top: 1px solid #edf2f7;
}

/* Chart Card */
.chart-card {
    background: white;
    border-radius: 16px;
    border: 1px solid var(--border);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
    height: 100%;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.1);
    }

    .chart-header {
        padding: 16px 20px;
        background: #f8fafc;
        border-bottom: 1px solid var(--border);
        font-weight: 600;
        color: var(--navy);
    }

    .chart-content {
        padding: 20px;
        min-height: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
    }
}

/* Sub-sections */
.sub-section {
    margin-top: 20px;
    margin-bottom: 16px;

    .sub-title {
        font-size: 18px;
        font-weight: 700;
        color: var(--navy);
        margin: 0 0 12px;
        display: flex;
        align-items: center;

        &::before {
            content: '';
            display: inline-block;
            width: 4px;
            height: 18px;
            background: var(--accent);
            margin-right: 10px;
            border-radius: 2px;
        }
    }
}

/* Custom Table Overrides */
    ::v-deep .el-table {
        background: transparent;
        font-size: 15px; /* Base font size increased */

        &::before { display: none; }

        th {
            background: var(--panel) !important;
            color: var(--navy);
            font-weight: 700;
            font-family: 'Playfair Display', serif;
            padding: 16px 0;
            border-bottom: 1px solid var(--border);
            font-size: 16px; /* Header font size */
        }

        td {
            padding: 14px 0;
            border-bottom: 1px solid var(--border);
        }

        .highlight-row {
        background-color: rgba(93, 135, 181, 0.1) !important;
    }

    tr:hover td {
        background-color: rgba(93, 135, 181, 0.05) !important;
    }
}

/* Pagination */
.custom-pagination {
    margin-top: 20px;
    text-align: right;
    padding: 10px;
}

/* Links */
.id-link, .gene-link {
    color: var(--accent);
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
}

.dl-icon {
    color: var(--accent);
    font-size: 18px;
    &:hover { color: var(--navy); }
}

/* Empty State */
.empty-state {
    background: rgba(255,255,255,0.4);
    border: 2px dashed var(--border);
    border-radius: 20px;
    padding: 60px 40px;
    text-align: center;
    color: var(--muted);

    i {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
    }

    p {
        font-size: 16px;
        max-width: 400px;
        margin: 0 auto;
        line-height: 1.5;
    }
}

/* Spacer */
.spacer { height: 100px; }

/* Transitions */
.el-fade-in-enter-active, .el-fade-in-leave-active {
    transition: opacity .3s;
}
.el-fade-in-enter, .el-fade-in-leave-to {
    opacity: 0;
}

/* Custom Tabs - Clean Style */
.custom-tabs {
    /deep/ .el-tabs__header {
        margin-bottom: 24px;
        border-bottom: 1px solid var(--border);
    }

    /deep/ .el-tabs__nav-wrap::after {
        display: none;
    }

    /deep/ .el-tabs__item {
        font-size: 16px;
        font-weight: 500;
        color: var(--muted);
        padding: 0 24px !important;
        height: 48px;
        line-height: 48px;
        transition: all 0.3s;

        &:hover {
            color: var(--accent);
        }

        &.is-active {
            color: var(--navy);
            font-weight: 600;
        }
    }

    /deep/ .el-tabs__active-bar {
        background-color: var(--accent);
        height: 3px;
    }
}
</style>
